import { getDocumentItemDetailsByDefaultLanguage } from "../../utils/document-item-translations";

export type QuoteItemSortField = "jobName" | "quantity" | "price";
export type QuoteItemSortDirection = "asc" | "desc";

export interface QuoteItemSortRule {
  id: string;
  field: QuoteItemSortField;
  direction: QuoteItemSortDirection;
}

export const QUOTE_ITEM_SORT_FIELDS: QuoteItemSortField[] = [
  "jobName",
  "quantity",
  "price",
];

export const MAX_QUOTE_ITEM_SORT_RULES = 3;

const DEFAULT_JOB_NAME_LABELS = [
  "job name",
  "jobname",
  "שם עבודה",
  "שם העבודה",
  "اسم العمل",
  "اسم الوظيفة",
  "auftragsname",
  "arbeitsname",
];

export interface SortQuoteItemsOptions {
  defaultLanguage?: string;
  jobNameLabels?: string[];
}

export interface SortedQuoteItem<T> {
  item: T;
  sourceIndex: number;
}

const normalizeLabel = (value: string): string =>
  value.replace(/\s+/g, " ").trim().toLowerCase();

const decodeHtml = (value: string): string =>
  value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));

const detailsToPlainText = (details: string): string => {
  const withBreaks = details
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n");
  return decodeHtml(withBreaks.replace(/<[^>]+>/g, " "))
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
};

const jobNameLabelSet = (extraLabels?: string[]): Set<string> => {
  const labels = new Set(DEFAULT_JOB_NAME_LABELS.map(normalizeLabel));
  extraLabels?.forEach((label) => {
    const normalized = normalizeLabel(String(label ?? ""));
    if (normalized && !normalized.includes(".")) {
      labels.add(normalized);
    }
  });
  return labels;
};

/**
 * Job name used for sorting.
 * A non-empty workName is used as-is.
 * When workName is missing, the labeled job name inside the details is used.
 */
export const getDisplayedJobName = (
  item: any,
  options?: SortQuoteItemsOptions,
): string => {
  const workName = String(item?.workName ?? item?.WorkName ?? "").trim();
  if (workName) {
    return workName;
  }

  const details = getDocumentItemDetailsByDefaultLanguage(
    item,
    options?.defaultLanguage,
  );
  const plain = detailsToPlainText(String(details ?? ""));

  const labels = jobNameLabelSet(options?.jobNameLabels);
  const lines = plain
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    const match = line.match(/^(.+?)\s*[:：]\s*(.*)$/);
    if (!match) {
      continue;
    }
    const label = normalizeLabel(match[1]);
    if (labels.has(label)) {
      return match[2].trim();
    }
  }

  return "";
};

const parseSortNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const cleaned = trimmed.replace(/[^\d,.\-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === ",") {
    return null;
  }
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized = cleaned;
  if (lastComma !== -1 && lastDot !== -1) {
    normalized =
      lastComma > lastDot
        ? cleaned.replace(/\./g, "").replace(",", ".")
        : cleaned.replace(/,/g, "");
  } else if (lastComma !== -1) {
    const fraction = cleaned.slice(lastComma + 1);
    normalized =
      fraction.length > 0 && fraction.length <= 2
        ? cleaned.replace(",", ".")
        : cleaned.replace(/,/g, "");
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const compareMissingLast = (
  aMissing: boolean,
  bMissing: boolean,
): number | null => {
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }
  return null;
};

const compareByRule = (
  a: any,
  b: any,
  rule: QuoteItemSortRule,
  options?: SortQuoteItemsOptions,
): number => {
  if (rule.field === "jobName") {
    const aName = getDisplayedJobName(a, options);
    const bName = getDisplayedJobName(b, options);
    const missing = compareMissingLast(!aName, !bName);
    if (missing !== null) {
      return missing;
    }
    const compared = aName.localeCompare(bName, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    return rule.direction === "desc" ? -compared : compared;
  }

  const rawA = rule.field === "quantity" ? a?.quantity : a?.finalPrice;
  const rawB = rule.field === "quantity" ? b?.quantity : b?.finalPrice;
  const aNumber = parseSortNumber(rawA);
  const bNumber = parseSortNumber(rawB);
  const missing = compareMissingLast(aNumber === null, bNumber === null);
  if (missing !== null) {
    return missing;
  }
  const compared = (aNumber as number) - (bNumber as number);
  return rule.direction === "desc" ? -compared : compared;
};

const activeRules = (rules: QuoteItemSortRule[]): QuoteItemSortRule[] => {
  const seen = new Set<QuoteItemSortField>();
  return rules.filter((rule) => {
    if (!QUOTE_ITEM_SORT_FIELDS.includes(rule.field) || seen.has(rule.field)) {
      return false;
    }
    seen.add(rule.field);
    return true;
  }).slice(0, MAX_QUOTE_ITEM_SORT_RULES);
};

export const sortQuoteItemsForDisplay = <T,>(
  items: T[] | null | undefined,
  rules: QuoteItemSortRule[],
  options?: SortQuoteItemsOptions,
): Array<SortedQuoteItem<T>> => {
  const source = Array.isArray(items) ? items : [];
  const decorated = source.map((item, sourceIndex) => ({
    item,
    sourceIndex,
    order: sourceIndex,
  }));
  const rulesToApply = activeRules(rules);
  if (rulesToApply.length === 0) {
    return decorated.map(({ item, sourceIndex }) => ({ item, sourceIndex }));
  }

  const sorted = [...decorated].sort((a, b) => {
    for (const rule of rulesToApply) {
      const compared = compareByRule(a.item, b.item, rule, options);
      if (compared !== 0) {
        return compared;
      }
    }
    return a.order - b.order;
  });

  return sorted.map(({ item, sourceIndex }) => ({ item, sourceIndex }));
};

let sortRuleSequence = 0;

export const createQuoteItemSortRuleId = (): string => {
  sortRuleSequence += 1;
  return `quote-item-sort-${sortRuleSequence}`;
};

export const nextQuoteItemSortField = (
  rules: QuoteItemSortRule[],
): QuoteItemSortField | null => {
  if (rules.length >= MAX_QUOTE_ITEM_SORT_RULES) {
    return null;
  }
  const used = new Set(rules.map((rule) => rule.field));
  return QUOTE_ITEM_SORT_FIELDS.find((field) => !used.has(field)) ?? null;
};

export interface PersistedQuoteItemSortLayer {
  field: QuoteItemSortField;
  direction: QuoteItemSortDirection;
}

const isSortField = (value: unknown): value is QuoteItemSortField =>
  value === "jobName" || value === "quantity" || value === "price";

export const normalizePersistedSortLayers = (
  layers: unknown,
): PersistedQuoteItemSortLayer[] => {
  if (!Array.isArray(layers)) {
    return [];
  }

  const normalized: PersistedQuoteItemSortLayer[] = [];
  const seen = new Set<QuoteItemSortField>();
  layers.forEach((layer) => {
    if (!layer || typeof layer !== "object" || normalized.length >= MAX_QUOTE_ITEM_SORT_RULES) {
      return;
    }
    const raw = layer as Record<string, unknown>;
    const field = raw.field ?? raw.Field;
    const direction = raw.direction ?? raw.Direction;
    if (!isSortField(field) || seen.has(field)) {
      return;
    }
    seen.add(field);
    normalized.push({
      field,
      direction: direction === "desc" ? "desc" : "asc",
    });
  });
  return normalized;
};

export const toPersistedSortLayers = (
  rules: QuoteItemSortRule[],
): PersistedQuoteItemSortLayer[] =>
  activeRules(rules).map(({ field, direction }) => ({ field, direction }));

export const rulesFromPersistedSortLayers = (
  layers: unknown,
): QuoteItemSortRule[] =>
  normalizePersistedSortLayers(layers).map((layer) => ({
    id: createQuoteItemSortRuleId(),
    field: layer.field,
    direction: layer.direction,
  }));

export const quoteItemSortLayersEqual = (
  rules: QuoteItemSortRule[],
  layers: unknown,
): boolean => {
  const current = toPersistedSortLayers(rules);
  const saved = normalizePersistedSortLayers(layers);
  return (
    current.length === saved.length &&
    current.every(
      (layer, index) =>
        layer.field === saved[index].field &&
        layer.direction === saved[index].direction,
    )
  );
};
